import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Fade,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import NewsCard from './NewsCard';
import SecurityIcon from '@mui/icons-material/Security';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import StorageIcon from '@mui/icons-material/Storage';
import RefreshIcon from '@mui/icons-material/Refresh';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0d3b66 0%, #1a5490 100%)',
  color: '#ffffff',
  padding: theme.spacing(4, 0),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(212, 175, 55, 0.15)',
  color: '#d4af37',
  fontWeight: 600,
  border: '1px solid #d4af37',
}));

const FilterToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  '&.Mui-selected': {
    backgroundColor: '#0d3b66',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#1a5490',
    },
  },
}));

const NewsFeed = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stats, setStats] = useState(null);

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;
  const maxArticles = 100;

  const categories = [
    { value: 'all', label: 'All News', icon: <RefreshIcon /> },
    { value: 'Infosec', label: 'Infosec', icon: <SecurityIcon /> },
    { value: 'AI', label: 'AI', icon: <SmartToyIcon /> },
    { value: 'DevOps', label: 'DevOps', icon: <DeveloperModeIcon /> },
    { value: 'Data', label: 'Data', icon: <StorageIcon /> },
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    setArticles([]);
    fetchArticles(0, true);
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter(article => article.category === selectedCategory));
    }
  }, [selectedCategory, articles]);

  const fetchArticles = async (currentOffset, isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      
      const categoryParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
      const response = await axios.get(`${API_BASE_URL}/api/news/articles?limit=${limit}&offset=${currentOffset}${categoryParam}`);
      
      if (response.data.success) {
        if (isInitial) {
          setArticles(response.data.articles);
        } else {
          setArticles(prev => [...prev, ...response.data.articles]);
        }
        
        const nextOffset = currentOffset + limit;
        if (nextOffset >= maxArticles || response.data.articles.length < limit) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        throw new Error(response.data.error || 'Failed to fetch articles');
      }
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError(err.message || 'Failed to load news articles. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextOffset = offset + limit;
    setOffset(nextOffset);
    fetchArticles(nextOffset, false);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/news/stats`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setSelectedCategory(newCategory);
    }
  };

  const getCategoryCount = (category) => {
    if (category === 'all') return articles.length;
    return articles.filter(article => article.category === category).length;
  };

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', pb: 4 }}>
      <HeaderBox>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            <SecurityIcon sx={{ fontSize: 40, mr: 2, verticalAlign: 'middle' }} />
            Cybersecurity News Feed
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            AI-curated security insights from trusted sources
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {stats && (
              <>
                <StatsChip label={`${stats.total_articles} Total Articles`} />
                <StatsChip label={`${stats.recent_articles} This Week`} />
              </>
            )}
            <StatsChip label="Updated Daily at 8:00 AM" />
          </Box>
        </Container>
      </HeaderBox>

      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
          <ToggleButtonGroup
            value={selectedCategory}
            exclusive
            onChange={handleCategoryChange}
            aria-label="news category filter"
            sx={{ flexWrap: 'wrap' }}
          >
            {categories.map((category) => (
              <FilterToggleButton
                key={category.value}
                value={category.value}
                aria-label={category.label}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {category.icon}
                  {category.label}
                  <Chip
                    label={getCategoryCount(category.value)}
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              </FilterToggleButton>
            ))}
          </ToggleButtonGroup>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : filteredArticles.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            No articles found for the selected category. New articles are published daily at 8:00 AM.
          </Alert>
        ) : (
          <Fade in={true} timeout={500}>
            <Grid container spacing={3}>
              {filteredArticles.map((article, index) => (
                <Grid item xs={12} sm={6} md={4} key={article.id || index}>
                  <NewsCard article={article} />
                </Grid>
              ))}
            </Grid>
          </Fade>
        )}

        {!loading && !error && filteredArticles.length > 0 && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
              {selectedCategory !== 'all' && ` in ${selectedCategory}`}
            </Typography>
          </Box>
        )}

        {hasMore && !loading && !error && (
          <Box sx={{ mt: 6, mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleLoadMore} 
              disabled={loadingMore}
              sx={{ 
                backgroundColor: '#0d3b66',
                '&:hover': { backgroundColor: '#1a5490' },
                fontWeight: 600,
                px: 6,
                py: 1.5,
                minWidth: '200px'
              }}
            >
              {loadingMore ? <CircularProgress size={24} color="inherit" /> : 'Load More Articles'}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default NewsFeed;
